import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { User, ArrowLeft } from "lucide-react";
import TheNav from "../Components/navbar";
import { uniqBy } from "lodash";
import SEO, { SITE_URL, breadcrumbSchema } from "../Components/SEO";
import { listPublicProfiles } from "../services/publicProfiles";

import apex from "../images/backgrounds/apex.webp";
import cod from "../images/backgrounds/cod.webp";
import fortnite from "../images/backgrounds/fortnite.webp";
import irl from "../images/backgrounds/irl.jpg";
import league from "../images/backgrounds/league.jpg";
import rivals from "../images/backgrounds/rivals.jpg";
import siege from "../images/backgrounds/siege.webp";
import tiktok from "../images/backgrounds/tiktok.png";
import valorant from "../images/backgrounds/valorant.webp";
import warzone from "../images/backgrounds/warzone.webp";
import youtube from "../images/backgrounds/youtube.jpg";

const MINT = "#10b981";
const PURPLE = "#a855f7";

export default function ViewCreators() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchCreators = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    try {
      const fetchedCreators = await listPublicProfiles({ role: "Creator" });
      if (fetchedCreators.length > 0) {

        setCreators((prev) =>
          uniqBy([...prev, ...fetchedCreators], "id")
        );

        setHasMore(false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to fetch creators:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBackgroundImage = (mainGame) => {
    const gameLower = normalizeGameName(mainGame);

    if (gameLower.includes("marvel rivals")) {
      return rivals;
    } else if (gameLower.includes("call of duty")) {
      return cod;
    } else if (gameLower.includes("rainbow six siege")) {
      return siege;
    } else if (gameLower.includes("apex legends")) {
      return apex;
    } else if (gameLower.includes("league of legends")) {
      return league;
    } else if (gameLower.includes("irl")) {
      return irl;
    } else if (gameLower.includes("youtube")) {
      return youtube;
    } else if (gameLower.includes("warzone")) {
      return warzone;
    } else if (gameLower.includes("valorant")) {
      return valorant;
    } else if (gameLower.includes("fortnite")) {
      return fortnite;
    } else if (gameLower.includes("tiktok")) {
      return tiktok;
    }

    return null;
  };

  const normalizeGameName = (mainGame) => {
    if (!mainGame) return "";
    if (typeof mainGame === "object") {
      return mainGame.categoryName?.toLowerCase?.() || "";
    }
    if (typeof mainGame === "string") {
      return mainGame.toLowerCase();
    }
    return "";
  };

  const getGameLabel = (mainGame, fallback = "Creator") => {
    if (!mainGame) return fallback;
    if (typeof mainGame === "object") {
      return mainGame.categoryName || fallback;
    }
    return mainGame;
  };

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      !loading &&
      hasMore
    ) {
      fetchCreators();
    }
  }, [loading, hasMore]);

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  function CreatorCard({ creator }) {
    const profileInfo = creator.profileInfo || {};
    const username = profileInfo.username || creator.id;
    const mainGame = profileInfo.mainGame || "";
    const gameLabel = getGameLabel(mainGame);
    const bgImage = getBackgroundImage(mainGame);
    const normalizedGame = normalizeGameName(mainGame);
    const accent =
      normalizedGame.includes("youtube") ||
      normalizedGame.includes("tiktok") ||
      normalizedGame.includes("irl")
        ? PURPLE
        : MINT;

    return (
      <Link
        to={`/creator/${username}`}
        className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1"
        style={{
          borderColor: "rgba(255,255,255,0.08)",
          backgroundImage: bgImage ? `url(${bgImage})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#141a17",
        }}
      >
        {/* overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.9) 100%)",
          }}
        />

        {/* category badge */}
        <div className="absolute left-3 top-3 z-10">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold"
            style={{
              backgroundColor: accent,
              color: "#0a0f0d",
            }}
          >
            {gameLabel}
          </span>
        </div>

        {/* avatar */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex h-24 w-24 items-center justify-center rounded-full border-2 ring-4 ring-black/30 overflow-hidden"
            style={{
              borderColor: accent,
              backgroundColor: "rgba(20,26,23,0.85)",
              backdropFilter: "blur(4px)",
            }}
          >
            {profileInfo.profilePicUrl ? (
              <img
                src={profileInfo.profilePicUrl}
                alt={`${username}'s profile`}
                className="h-full w-full object-cover"
                fetchPriority="high"
                decoding="async"
                loading="eager"
              />
            ) : (
              <User className="h-10 w-10 text-zinc-400" />
            )}
          </div>
        </div>

        {/* username */}
        <div className="absolute inset-x-0 bottom-0 z-10 p-4 text-center">
          <p className="text-base font-semibold text-white drop-shadow">
            @{username}
          </p>

          <p
            className="mt-1 text-xs font-medium opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ color: accent }}
          >
            View profile →
          </p>
        </div>
      </Link>
    );
  }

  return (
    <>
      <SEO
        title="Browse Twitch Streamers Looking for Editors | Kre8"
        description="Browse Twitch streamers, gaming creators, YouTube gaming channels, and short-form creators looking for gaming video editors."
        canonicalPath="/creators"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Browse Twitch Streamers Looking for Editors",
            description:
              "Browse Twitch streamers, gaming creators, YouTube gaming channels, and short-form creators looking for gaming video editors.",
            url: `${SITE_URL}/creators`,
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Creators", path: "/creators" },
          ]),
        ]}
      />

      <TheNav />

      <main
        className="min-h-screen px-6 py-12 sm:py-16"
        style={{
          backgroundColor: "#0a0f0d",
          color: "#e5e7eb",
        }}
      >
        <div className="mx-auto max-w-6xl">
          <Link
            to="/for-editors"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>

          <div className="mt-8 text-center">
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: PURPLE }}
            >
              Creators looking for editors
            </p>

            <h1 className="mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Available Creators
            </h1>

            <p className="mx-auto mt-4 max-w-xl text-zinc-400">
              Browse Twitch streamers, gaming creators, YouTube gaming
              channels, and short-form creators looking for editing help.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {creators.map((creator) => (
              <CreatorCard
                key={creator.id}
                creator={creator}
              />
            ))}
          </div>

          {loading && (
            <p className="mt-8 text-center text-zinc-500">
              Loading creators...
            </p>
          )}
        </div>
      </main>
    </>
  );
}
